import { cloneElement, forwardRef, isValidElement, type MouseEvent, type ReactNode } from 'react';

import { ButtonVariant, type ButtonVariantType } from './types';
import { cn } from '../../utils/cn';
import type { EventBehaviorProps } from '../../utils/EventBehavior';
import { handleEventBehavior } from '../../utils/EventBehavior';

export interface ButtonProps extends React.ComponentPropsWithoutRef<'button'>, EventBehaviorProps {
    variant?: ButtonVariantType;
    children?: ReactNode;
    iconLeft?: ReactNode;
    iconRight?: ReactNode;
    showOutline?: boolean;
    showBorder?: boolean;
    ariaLabel?: string;
}

const resolveAriaLabel = (
    ariaLabel: string | undefined,
    children: ReactNode,
    isIconOnly: boolean,
): string | undefined => {
    if (ariaLabel) return ariaLabel;
    if (typeof children === 'string' || typeof children === 'number') return String(children);
    // For element/composite children the accessible name is derived from the visible content,
    // so we must not stringify them (it would produce e.g. "[object Object]").
    return isIconOnly ? 'Button' : undefined;
};

const midnightOutlineStyles = cn(
    'bg-white text-midnight outline outline-1 outline-offset-[-1px] outline-midnight',
    'hover:bg-surface-light hover:text-midnight-hover hover:outline hover:outline-1 hover:outline-midnight-hover',
    'focus-visible:outline-2 focus-visible:outline-blue-focus focus-visible:bg-surface-light',
    'active:bg-surface-light active:text-midnight-active active:outline active:outline-1 active:outline-midnight-active',
    'disabled:bg-surface-light disabled:text-midnight-disabled-text disabled:outline disabled:outline-1 disabled:outline-midnight-disabled',
);

const variantStyles: Record<ButtonVariantType, string> = {
    [ButtonVariant.PRIMARY]: cn(
        'bg-midnight text-white',
        'hover:bg-midnight-hover',
        'focus-visible:outline-2 focus-visible:outline-blue-focus',
        'active:bg-midnight-active',
        'disabled:bg-midnight-disabled disabled:text-midnight-disabled-text',
    ),
    [ButtonVariant.PRIMARY_OUTLINE]: midnightOutlineStyles,
    [ButtonVariant.SECONDARY]: cn(
        'bg-white text-midnight outline outline-1 outline-offset-[-1px] outline-grey-outline-light',
        'hover:bg-surface-light hover:text-midnight-hover hover:outline hover:outline-1 hover:outline-midnight-hover',
        'focus-visible:outline-2 focus-visible:outline-blue-focus focus-visible:bg-surface-light',
        'active:bg-surface-light active:text-midnight-active active:outline active:outline-1 active:outline-midnight-active',
        'disabled:bg-surface-light disabled:text-midnight-disabled-text disabled:outline disabled:outline-1 disabled:outline-midnight-disabled',
    ),
    // Figma "Secondary Outlined Button": same look as PRIMARY_OUTLINE, own name so secondary actions stay secondary
    [ButtonVariant.SECONDARY_DARK]: midnightOutlineStyles,
};

/**
 * A styled button component with support for variants and icons.
 *
 * @param variant - Visual style variant. Defaults to {@link ButtonVariant.PRIMARY}.
 * @param children - Button label/content.
 * @param iconLeft - Icon element to display before the label.
 * @param iconRight - Icon element to display after the label.
 * @param showOutline - *DEPRECATED* Whether to show outline. When undefined, uses the variant's default outline behavior. When explicitly set to false, removes the outline.
 * @param showBorder - *DEPRECATED* Whether to show border. When true, removes outline and adds a border. Border color matches the variant's outline color.
 * @param className - Additional CSS classes to merge with default styles.
 *
 * @example
 * <Button>Submit</Button>
 *
 * @example
 * <Button variant={ButtonVariant.SECONDARY} iconLeft={<LuPlus />}>Add Item</Button>
 *
 * @example
 * <Button variant={ButtonVariant.SECONDARY} showBorder>With Border</Button>
 *
 * @example
 * <Button iconLeft={<LuPlus />} aria-label="Add" />
 *
 * @see {@link ButtonVariant} for all available variants.
 *
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            ariaLabel,
            children,
            className,
            eventPreventDefault,
            eventStopPropagation,
            iconLeft,
            iconRight,
            onClick,
            showBorder,
            showOutline,
            variant = ButtonVariant.PRIMARY,
            ...props
        },
        ref,
    ) => {
        const isIconOnly = !children && (iconLeft || iconRight);

        const borderClasses = cn(
            variant === ButtonVariant.PRIMARY_OUTLINE || variant === ButtonVariant.SECONDARY_DARK
                ? 'border border-midnight'
                : 'border border-grey-outline-light',
            'hover:border hover:border-midnight-hover',
            'active:border active:border-midnight-active',
            'disabled:border disabled:border-midnight-disabled',
        );

        const shouldRemoveOutline = showOutline === false || showBorder === true;

        return (
            <button
                ref={ref}
                aria-label={resolveAriaLabel(ariaLabel, children, Boolean(isIconOnly))}
                className={cn(
                    'relative inline-flex items-center',
                    isIconOnly ? 'justify-center' : 'justify-center gap-2',
                    'rounded text-sm transition-colors select-none',
                    isIconOnly ? 'p-1.5' : 'py-1.5 px-2.5',
                    'disabled:cursor-not-allowed',
                    variantStyles[variant],
                    shouldRemoveOutline && 'outline-0 hover:outline-0 active:outline-0 disabled:outline-0',
                    showBorder === true && borderClasses,
                    className,
                )}
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    handleEventBehavior(e, { eventPreventDefault, eventStopPropagation });
                    onClick?.(e);
                }}
                {...props}
            >
                {isValidElement<React.HTMLAttributes<HTMLElement>>(iconLeft)
                    ? cloneElement(iconLeft, { ...iconLeft.props, 'aria-hidden': 'true' })
                    : iconLeft}
                {children}
                {isValidElement<React.HTMLAttributes<HTMLElement>>(iconRight)
                    ? cloneElement(iconRight, { ...iconRight.props, 'aria-hidden': 'true' })
                    : iconRight}
            </button>
        );
    },
);

Button.displayName = 'Button';
